'use client';

import { useState } from 'react';

interface Author {
  name: string;
  handle: string;
  imageUrl: string;
  customHandle?: string;
}

interface Testimonial {
  body: string;
  author: Author;
  featured?: boolean;
}

const testimonials: Testimonial[] = [
{
    body: "Love how Preswald keeps everything in-browser with Pyodide and still lets you ship real, interactive reports. The AI code refactor and one-click deploys are super handy",
    author: {
        name: "Justin Bao",
        handle: "https://www.producthunt.com/products/preswald-2?comment=4578689#preswald-2",
        imageUrl: "images/testimonials/testimonial-8.png",
        customHandle: "@justin_bao"
    },
    },
  
  {
    body: "Preswald is an amazing framework that makes development and deployment of data apps incredibly smooth. I could get started in just a few minutes. It saves time and effort required to productionize notebooks and ad-hoc analysis.",
    author: {
      name: "Prakhar Nag",
      handle: "https://github.com/prakharnag",
      imageUrl: "images/testimonials/testimonial-2.png",
    },
  },
  {
    body: "Preswald has completely redefined how I work, turning Python scripts into interactive apps, dashboards, and internal tools has never been this effortless. The local development, one-command deployment, and instant sharing have taken my productivity to a whole new level.",
    author: {
      name: "Pramod Nagaraj",
      handle: "https://github.com/pramodnagaraj",
      imageUrl: "images/testimonials/testimonial-3.png",
    },
    featured: true,
  },

  {
    body: "Before Preswald, integrating notebooks with 3D libraries and managing deployment challenges was a constant struggle. With Preswald, my project effortlessly brought together interactive Python analysis and visualizations.",
    author: {
      name: "Varun Mehta",
      handle: "https://github.com/varunmehta14",
      imageUrl: "images/testimonials/testimonial-7.png",
    },
  },
  {
    body: "I used to use Looker and it was such a mess to keep dashboards and metrics up to date, so having everything in Python is awesome and really lowers the barrier to entry!",
    author: {
      name: "Steph Wolski",
      handle: "https://www.producthunt.com/products/preswald-2?comment=4578527#preswald-2",
      imageUrl: "images/testimonials/testimonial-10.png",
      customHandle: "@slowki"
    },
  },
  {
    body: "Preswald made it very easy for me to build and deploy a dashboard in just 20 minutes. I didn't even need to write any frontend code. The visuals and deployment tools are excellent and user-friendly. Highly recommended!",
    author: {
      name: "Shree Bohara",
      handle: "https://github.com/ShreeBohara",
      imageUrl: "images/testimonials/testimonial-1.png",
    },
  },
  {
    body: "The framework is intuitive, well-documented, and makes deployment very very easy. It's perfect for prototyping and sharing data apps.",
    author: {
      name: "Lokesh Velayudham",
      handle: "https://github.com/lokeshvelayudham",
      imageUrl: "images/testimonials/testimonial-6.png",
    },
  },
  {
    body: "serious game changer and time saver for startups who don't have the team and a lot of resources to spend. Love this!",
    author: {
      name: "Rohan Pathak",
      handle: "https://www.producthunt.com/products/preswald-2?comment=4577877#preswald-2",
      imageUrl: "images/testimonials/testimonial-9.png",
      customHandle: "@rohan_pathak2"
    },
  },

  {
    body: "I spent so many hours building dashboards in previous business analyst and data roles - this is such a time saver",
    author: {
      name: "Amy Yan",
      handle: "https://www.producthunt.com/products/preswald-2?comment=4576675#preswald-2",
      imageUrl: "images/testimonials/testimonial-11.png",
      customHandle: "@amysunyan"
    },
  },
  {
    body: "Preswald makes it easy to build high-performance backend visualizations without the usual frontend headache. Being part of the early community has been a genuinely fun and rewarding experience.",
    author: {
      name: "Trevor Wilson",
      handle: "https://github.com/BloggerBust",
      imageUrl: "images/testimonials/testimonial-4.png",
    },
  },
  {
    body: "turning Jupyter-style workflows into full, shareable data apps without backend hassle is a huge win. The in-browser Python runtime and built-in UI components make it incredibly smooth",
    author: {
      name: "Mostafizur Ashique",
      handle: "https://www.producthunt.com/products/preswald-2?comment=4578852#preswald-2",
      imageUrl: "images/testimonials/testimonial-12.png",
      customHandle: "@mostafizur_rahman_ashique_"
    },
  },
  {
    body: "I recently used Preswald and was amazed by how effortlessly I could build and deploy a fully interactive data app using just Python and SQL. It was nice to not have to write any front-end code, but still get a clean UI.",
    author: {
      name: "Nandini Morla",
      handle: "https://github.com/morlanandini",
      imageUrl: "images/testimonials/testimonial-5.png",
    },
  },
];

const PlatformIcon = ({ handle }: { handle: string }) => {
  const isProductHunt = handle.includes('producthunt.com');
  
  return isProductHunt ? (
    <img src="/images/ph.png" alt="Product Hunt" className="h-5 w-5 object-contain" />
  ) : (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-5 w-5 text-gray-400"
      fill="currentColor"
    >
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  );
};

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
  const { author, body } = testimonial;
  const displayHandle = author.customHandle || 
    (author.handle.includes('github.com') ? `@${author.handle.split('/').pop()}` : '@producthunt');

  return (
    <div className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-md p-6 transition-transform duration-300 hover:transform hover:rotate-1 hover:scale-[1.02]">
      <div className="flex items-center mb-4">
        <img
          src={author.imageUrl}
          alt={author.name}
          className="h-10 w-10 rounded-full border border-gray-200 object-cover mr-3"
        />
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900 text-sm">{author.name}</span>
          <a
            href={author.handle}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:underline"
          >
            {displayHandle}
          </a>
        </div>
        <span className="ml-auto">
          <PlatformIcon handle={author.handle} />
        </span>
      </div>
      <blockquote className="text-gray-700 text-base mb-2">
        {body}
      </blockquote>
    </div>
  );
};

const shimmerAnimation = `
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.animate-pulse {
  animation: pulse 0.3s ease-in-out;
  animation-iteration-count: infinite;
  animation-duration: 2s;
  animation-direction: alternate;
  animation-play-state: running;
  animation-timeline: auto;
  animation-range: normal;
}

@media (prefers-reduced-motion: reduce) {
  .animate-pulse {
    animation: none;
  }
}`;

export default function Testimonials() {
  const [showAll, setShowAll] = useState(false);
  const initialCount = 6;
  const displayedTestimonials = showAll ? testimonials : testimonials.slice(0, initialCount);

  return (
    <>
      <style>{shimmerAnimation}</style>
      <section className="relative isolate bg-background border border-[#e0e0e0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 sm:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold leading-7 text-primary">
              Trusted by Builders
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="inline-block w-6 h-6 text-red-500 mr-2 animate-pulse"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
              Wall of Love
            </p>
          </div>
          
          <div className="mx-auto mt-16 max-w-7xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayedTestimonials.map((testimonial, index) => (
                <TestimonialCard key={`${testimonial.author.handle}-${index}`} testimonial={testimonial} />
              ))}
            </div>
            
            {testimonials.length > initialCount && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showAll ? 'Show Less' : 'Show More'}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}